import { Component } from 'react';

import { GetEarlyRepaymentSimulationResponse } from '@iwoca/lapi-client/edge';
import { FormattedMessage } from 'react-intl';

import messages from './RepaymentAmountBreakdown.messages';
import styles from './RepaymentAmountBreakdown.module.css';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';

type TProps = {
    loadingNewRepaymentSchedule: boolean | null;
    showSchedule?: (
        earlyRepaymentSimulationSchedule: GetEarlyRepaymentSimulationResponse
    ) => void;
    earlyRepaymentSimulationSchedule?: GetEarlyRepaymentSimulationResponse;
    remainingOutstandingBalance: number | null;
    numberOfNextRepayments: number | null;
    reducedNextRepayment: number | null;
    reducedNextRepaymentAmount: number | null;
    nextRepaymentStatus:
        | 'NORMAL'
        | 'PARTIAL_FUTURE'
        | 'PARTIAL_TODAY'
        | 'EXACT_AMOUNT'
        | null;
    followingRepaymentStatus: 'EQUAL' | 'FULL_REPAYMENT' | null;
    hasFollowingRepaymentsLeft?: boolean;
    followingRepaymentAmount?: number | null;
    error: boolean | null;
};

export class RepaymentAmountBreakdown extends Component<TProps> {
    handleShowSchedule = () => {
        if (!this.props.loadingNewRepaymentSchedule) {
            this.props.showSchedule!(
                this.props.earlyRepaymentSimulationSchedule!
            );
        }
    };

    getClassName() {
        if (this.props.loadingNewRepaymentSchedule) {
            return styles.RepaymentAmountBreakdownLoading;
        }

        return styles.RepaymentAmountBreakdown;
    }

    renderOutstandingBalance() {
        const { remainingOutstandingBalance } = this.props;

        if (remainingOutstandingBalance! >= 0) {
            return (
                <div data-testid="repayment-outstanding-balance">
                    <h3 className={styles.title}>
                        <FormattedMessage {...messages.remainingOutstanding} />
                    </h3>
                    <div className={styles.amount}>
                        <MoneyWithDecimals
                            className={styles.moneyLarge}
                            value={remainingOutstandingBalance!}
                            decimalWithSeparatorClassName={
                                styles.decimalsWithSeparator
                            }
                        />
                    </div>
                </div>
            );
        }
    }

    renderShowSchedule() {
        return (
            <div>
                <span
                    className={styles.showSchedule}
                    onClick={this.handleShowSchedule}
                >
                    <FormattedMessage {...messages.showSchedule} />
                </span>
            </div>
        );
    }

    renderNextRepayments() {
        const { numberOfNextRepayments, reducedNextRepayment } = this.props;

        const reducedRepaymentText = (
            <FormattedMessage
                {...messages.reducedRepaymentText}
                values={{ numberOfNextRepayments }}
            />
        );
        const reducedNextRepaymentAmount = (
            <FormattedCurrency value={this.props.reducedNextRepaymentAmount!} />
        );

        const reducedNextRepaymentBreakdownText = () => {
            switch (this.props.nextRepaymentStatus) {
                case 'NORMAL':
                    return (
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                {...messages.normal}
                                values={{
                                    reducedRepaymentText: (
                                        <b>{reducedRepaymentText}</b>
                                    ),
                                    nextReducedRepaymentText: (
                                        <b>
                                            <FormattedMessage
                                                {...messages.nextReducedRepaymentText}
                                                values={{
                                                    reducedNextRepayment,
                                                    reducedNextRepaymentAmount
                                                }}
                                            />
                                        </b>
                                    )
                                }}
                            />
                        </p>
                    );
                case 'PARTIAL_FUTURE':
                    return (
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                {...messages.partialNextRepayment}
                                values={{ reducedNextRepaymentAmount }}
                            />
                        </p>
                    );
                case 'PARTIAL_TODAY':
                    return (
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                {...messages.partialTodayNextRepayment}
                                values={{ reducedNextRepaymentAmount }}
                            />
                        </p>
                    );
                case 'EXACT_AMOUNT':
                    return (
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                {...messages.exactAmount}
                                values={{ reducedRepaymentText }}
                            />
                        </p>
                    );
            }
        };

        const breakdownText = reducedNextRepaymentBreakdownText();

        return <div className={styles.breakdown}>{breakdownText}</div>;
    }

    renderFollowingRepayments() {
        const { followingRepaymentStatus, hasFollowingRepaymentsLeft } =
            this.props;

        if (!hasFollowingRepaymentsLeft) {
            return <FormattedMessage {...messages.noFurtherRepayments} />;
        }

        switch (followingRepaymentStatus) {
            case 'EQUAL':
                return (
                    <div>
                        <p className={styles.breakdownText}>
                            <FormattedMessage
                                {...messages.equal}
                                values={{
                                    followingRepaymentAmount: (
                                        <FormattedCurrency
                                            value={
                                                this.props
                                                    .followingRepaymentAmount!
                                            }
                                        />
                                    )
                                }}
                            />
                        </p>
                    </div>
                );

            case 'FULL_REPAYMENT':
                return (
                    <div>
                        <FormattedMessage {...messages.repaymentsCovered} />
                    </div>
                );
        }
    }

    renderDetails() {
        if (this.props.error) {
            const showSchedule = this.renderShowSchedule();
            return (
                <>
                    <div>
                        <FormattedMessage {...messages.unsupported} />
                    </div>
                    {showSchedule}
                </>
            );
        } else if (this.props.remainingOutstandingBalance! > 0) {
            const nextRepayment = this.renderNextRepayments();
            const followingRepayment = this.renderFollowingRepayments();
            const showSchedule = this.renderShowSchedule();

            return (
                <div
                    className={styles.details}
                    data-testid="repayment-breakdown"
                >
                    {nextRepayment}
                    {followingRepayment}
                    {showSchedule}
                </div>
            );
        } else if (this.props.remainingOutstandingBalance === 0) {
            const followingRepayment = this.renderFollowingRepayments();
            return <div className={styles.details}>{followingRepayment}</div>;
        }
    }

    render() {
        const outstandingBalance = this.renderOutstandingBalance();
        const details = this.renderDetails();

        return (
            <div className={this.getClassName()}>
                {outstandingBalance}
                {details}
            </div>
        );
    }
}
